import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, input } from '@angular/core';
import { AssetDto, DisplayGuestEmail, DisplayUserEmail, GalleryDto } from '@nusign/picflow-backend-types';
import { combineLatestWith, forkJoin, map, of, shareReplay, switchMap, take, withLatestFrom } from 'rxjs';
import { GROWTHBOOK } from '../../../growthbook';
import { DialogService } from '../../../services/dialog.service';
import { GalleryMemberService } from '../../../services/gallery-member.service';
import { OrganizationService } from '../../../services/organization.service';
import { StateService } from '../../../services/state.service';
import { VisitorService } from '../../../services/visitor.service';
import { WatcherService, WatchingPerson } from '../../../services/watcher.service';
import { AvatarComponent } from '../../../shared/components/avatar/avatar.component';
import { ContextMenuComponent } from '../../../shared/components/context-menu/context-menu.component';
import { LoadingIndicatorComponent } from '../../../shared/components/loading-indicator/loading-indicator.component';
import { SvgComponent } from '../../../shared/components/svg/svg.component';
import { ButtonDirective } from '../../../shared/directives/button.directive';
import { GallerySubscribersDialogComponent } from '../../dialogs/gallery-subscribers-dialog/gallery-subscribers-dialog.component';

@Component({
  selector: 'app-asset-watchers-context-menu',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ButtonDirective, SvgComponent, ContextMenuComponent, AvatarComponent, AsyncPipe, LoadingIndicatorComponent],
  templateUrl: './asset-watchers-context-menu.component.html',
  styleUrl: './asset-watchers-context-menu.component.scss',
})
export class AssetWatchersContextMenuComponent {
  public growthBook = inject(GROWTHBOOK);
  public state = inject(StateService);
  private watcherService = inject(WatcherService);
  private visitorService = inject(VisitorService);
  private galleryMemberService = inject(GalleryMemberService);
  private organizationService = inject(OrganizationService);
  private dialog = inject(DialogService);

  galleryPermissions = input.required<GalleryDto['permissions']>();
  isTeamMember = input.required<boolean>();
  gallery = input.required<GalleryDto>();
  asset = input.required<AssetDto>();
  myId = (this.state.user || this.state.guest)?.id;

  totalWatcherCount$ = of(undefined).pipe(
    switchMap(() => {
      return this.watcherService.getGalleryWatchers(this.gallery().id).pipe(
        withLatestFrom(this.watcherService.getAssetWatchers(this.asset().id, this.gallery().id, false)),
        map(([galleryWatchers, assetWatchers]) => {
          return [...galleryWatchers, ...assetWatchers].filter(w => w.id !== this.myId);
        }),
        map(allWatchers => allWatchers.length),
        shareReplay(1)
      );
    })
  );

  idEmailMap$ = of(undefined).pipe(
    switchMap(() => {
      if (!this.isTeamMember() || !this.galleryPermissions().can_edit) return of({});
      return forkJoin([
        this.visitorService.getGalleryVisitors(this.gallery().id).pipe(take(1)),
        this.galleryMemberService.getAllGalleryMembers(true).pipe(take(1)),
        this.organizationService.getMembers().pipe(take(1)),
      ]).pipe(
        map(([visitors, galleryMembers, members]) => {
          const idEmailMap = {};
          const allUsers = [
            ...visitors.map(v => (v.user as DisplayUserEmail) || (v.guest as DisplayGuestEmail)),
            ...galleryMembers.map(g => g.user as DisplayUserEmail),
            ...members,
          ];
          allUsers.forEach(user => (idEmailMap[user.id] = user.email));
          return idEmailMap;
        })
      );
    }),
    shareReplay(1)
  );

  assetWatchers$ = of(undefined).pipe(
    switchMap(() => {
      return this.watcherService.getAssetWatchers(this.asset().id, this.gallery().id, false).pipe(
        combineLatestWith(this.idEmailMap$),
        map(([watchers, idEmailMap]) =>
          watchers
            .map(watcher => {
              return { ...watcher, email: idEmailMap[watcher.id] };
            })
            .filter(watcher => watcher.id !== this.myId)
        )
      );
    })
  );

  galleryWatchers$ = of(undefined).pipe(
    switchMap(() => {
      return this.watcherService.getGalleryWatchers(this.gallery().id).pipe(
        combineLatestWith(this.idEmailMap$),
        map(([watchers, idEmailMap]) =>
          watchers
            .map(watcher => {
              return { ...watcher, email: idEmailMap[watcher.id] };
            })
            .filter(watcher => watcher.id !== this.myId)
        )
      );
    })
  );

  deleteWatcher(watcher: WatchingPerson) {
    this.watcherService.removeWatcher$.next({ id: watcher.watcher_id, galleryId: this.gallery().id });
  }

  openGallerySubscribersDialog() {
    this.dialog.open(GallerySubscribersDialogComponent, { gallery: this.gallery() });
  }
}
